/**
 * APX WorkCenter — App Router
 * Design: "Gold Standard" — Premium Editorial Finance
 * Role-based routing: login → role-specific dashboard
 *
 * ROUTING RULES (to prevent React Error #185):
 *
 * 1. NEVER use wouter's <Redirect> — it calls navigate() in useIsomorphicLayoutEffect
 *    (synchronous during React commit phase) → setState → infinite loop.
 *
 * 2. NEVER call wouter's navigate() inside useEffect for auth redirects — wouter
 *    monkey-patches history.pushState to call forceStoreRerender, which triggers
 *    a React re-render of every component using useLocation(). If those components
 *    contain redirect logic, you get an infinite loop.
 *
 * 3. CORRECT APPROACH: Render the correct component directly based on auth state.
 *    No navigation happens during the render cycle. The only "redirect" that happens
 *    is window.location.replace() for the initial unauthenticated load to /login,
 *    which is a one-shot browser navigation — not a React state update.
 */

import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch } from "wouter";
import { useEffect } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PreviewProvider, useEffectiveRole } from "./contexts/PreviewContext";

// Pages
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import AdminDashboard from "./pages/dashboards/AdminDashboard";
import ControllerDashboard from "./pages/dashboards/ControllerDashboard";
import BookkeepingManagerDashboard from "./pages/dashboards/BookkeepingManagerDashboard";
import BookkeeperDashboard from "./pages/dashboards/BookkeeperDashboard";
import NotFound from "./pages/NotFound";
import AdminSettings from "./pages/AdminSettings";
import TaxDivisionDashboard from "./pages/dashboards/TaxDivisionDashboard";
import SearchMockup from "./pages/SearchMockup";
import StaffManagement from "./pages/StaffManagement";
import EmailInbox from "./pages/EmailInbox";
import GoogleDrive from "./pages/GoogleDrive";
import TaskRequestList from "./pages/TaskRequestList";
import DocumentFolder from "./pages/DocumentFolder";
import SmsInbox from "./pages/SmsInbox";
import XeroReports from "./pages/XeroReports";
import { useAutoReload } from "./hooks/useAutoReload";
import { trpc } from "./lib/trpc";
import XeroClientManagement from "./pages/XeroClientManagement";
import PlatformCosts from "./pages/PlatformCosts";
import Payments from "./pages/Payments";
import ClientAlerts from "./pages/ClientAlerts";
import ClientAnalytics from "./pages/ClientAnalytics";
import ClientAnalyticsOverview from "./pages/ClientAnalyticsOverview";
import RedTeam from "./pages/RedTeam";
import RedTeamV2 from "./pages/RedTeamV2";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import TransactionReview from "./pages/TransactionReview";
import ReconciliationWorkspace from "./pages/ReconciliationWorkspace";
import Clients from "./pages/Clients";
import MyTasks from "./pages/MyTasks";
import AIWritingStudio from "./pages/AIWritingStudio";
import AIResearch from "./pages/AIResearch";
import PortfolioWorkReport from "./pages/PortfolioWorkReport";
import PricingCalculator from "./pages/PricingCalculator";
import DepreciationWorksheet from "./pages/DepreciationWorksheet";
import TeamNotes from "./pages/TeamNotes";

// Loading spinner
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div
          className="w-12 h-12 rounded-full border-2 border-t-transparent mx-auto mb-4 animate-spin"
          style={{ borderColor: "oklch(0.92 0.005 240 / 30%)", borderTopColor: "oklch(0.82 0.01 240)" }}
        />
        <p className="text-sm text-muted-foreground">Loading APX WorkCenter…</p>
      </div>
    </div>
  );
}

// Role-based dashboard dispatcher — respects preview role if set.
// Renders Login directly if no user — NO navigation call, no history.pushState.
function RoleDashboard() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole(user?.role ?? "bookkeeper");
  if (!user) return <Login />;

  switch (effectiveRole) {
    case "super_admin":
    case "admin":
      return <AdminDashboard />;
    case "controller":
      return <ControllerDashboard />;
    case "asst_controller":
      return <ControllerDashboard />;
    case "bookkeeping_manager":
      return <BookkeepingManagerDashboard />;
    case "bookkeeper_supervisor":
      return <BookkeepingManagerDashboard />;
    case "bookkeeper":
      return <BookkeeperDashboard />;
    case "tax_division":
      return <TaxDivisionDashboard />;
    default:
      return <Login />;
  }
}

// Protected route: shows LoadingScreen during auth check, Login if not authenticated.
// Renders the component directly — NO navigate() call, no history.pushState.
function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!user) return <Login />;
  return <Component />;
}

// Public route: shows LoadingScreen during auth check.
// If already logged in, renders the dashboard directly at the current URL.
// NO navigate() call — avoids the forceStoreRerender loop entirely.
function PublicRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (user) return <RoleDashboard />;
  return <Component />;
}

// ─── Stable route wrappers ────────────────────────────────────────────────────
// Each wrapper is a named component defined at module scope so its reference
// never changes between renders. Inline arrow functions in Route `component`
// props create new references every render → wouter remounts → infinite loop.

function LoginRoute() { return <PublicRoute component={Login} />; }
function DashboardRoute() { return <ProtectedRoute component={RoleDashboard} />; }
function SettingsRoute() { return <ProtectedRoute component={AdminSettings} />; }
function IntelligenceRoute() { return <ProtectedRoute component={SearchMockup} />; }
function StaffRoute() { return <ProtectedRoute component={StaffManagement} />; }
function EmailRoute() { return <ProtectedRoute component={EmailInbox} />; }
function DriveRoute() { return <ProtectedRoute component={GoogleDrive} />; }
function MyTasksRoute() { return <ProtectedRoute component={MyTasks} />; }
function TaskRequestsRoute() { return <ProtectedRoute component={TaskRequestList} />; }
function DocumentsRoute() { return <ProtectedRoute component={DocumentFolder} />; }
function SmsRoute() { return <ProtectedRoute component={SmsInbox} />; }
function XeroReportsRoute() { return <ProtectedRoute component={XeroReports} />; }
function XeroClientMgmtRoute() { return <ProtectedRoute component={XeroClientManagement} />; }
function CostsRoute() { return <ProtectedRoute component={PlatformCosts} />; }
function PaymentsRoute() { return <ProtectedRoute component={Payments} />; }
function ClientAlertsRoute() { return <ProtectedRoute component={ClientAlerts} />; }
function ClientAnalyticsRoute() { return <ProtectedRoute component={ClientAnalytics} />; }
function ClientAnalyticsOverviewRoute() { return <ProtectedRoute component={ClientAnalyticsOverview} />; }
function RedTeamRoute() { return <ProtectedRoute component={RedTeam} />; }
function RedTeamV2Route() { return <ProtectedRoute component={RedTeamV2} />; }
function TransactionReviewRoute() { return <ProtectedRoute component={TransactionReview} />; }
function ReconciliationRoute() { return <ProtectedRoute component={ReconciliationWorkspace} />; }
function ClientsRoute() { return <ProtectedRoute component={Clients} />; }
function AIWritingRoute() { return <ProtectedRoute component={AIWritingStudio} />; }
function AIResearchRoute() { return <ProtectedRoute component={AIResearch} />; }
function PortfolioReportRoute() { return <ProtectedRoute component={PortfolioWorkReport} />; }
function PricingCalculatorRoute() { return <ProtectedRoute component={PricingCalculator} />; }
function DepreciationWorksheetRoute() { return <ProtectedRoute component={DepreciationWorksheet} />; }
function TeamNotesRoute() { return <ProtectedRoute component={TeamNotes} />; }

// HomeRedirect: visiting "/" while unauthenticated → browser navigates to /login.
// visiting "/" while authenticated → render dashboard directly.
// This is the ONLY place we use window.location — it's a one-shot browser navigation,
// not a React state update, so it cannot cause a render loop.
function HomeRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (user) return <RoleDashboard />;
  // One-shot browser navigation to /login — not a React state update
  window.location.replace("/login");
  return <LoadingScreen />;
}

function SmsRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!user) return <Login />;
  window.location.replace("/sms-inbox");
  return <LoadingScreen />;
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={LoginRoute} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/dashboard" component={DashboardRoute} />
      <Route path="/settings" component={SettingsRoute} />
      <Route path="/intelligence" component={IntelligenceRoute} />
      <Route path="/search-preview" component={IntelligenceRoute} />
      <Route path="/settings/staff" component={StaffRoute} />
      <Route path="/email-inbox" component={EmailRoute} />
      <Route path="/drive" component={DriveRoute} />
      <Route path="/my-tasks" component={MyTasksRoute} />
      <Route path="/task-requests" component={TaskRequestsRoute} />
      <Route path="/documents" component={DocumentsRoute} />
      <Route path="/sms-inbox" component={SmsRoute} />
      <Route path="/communications" component={SmsRedirect} />
      <Route path="/xero-reports" component={XeroReportsRoute} />
      <Route path="/xero-client-management" component={XeroClientMgmtRoute} />
      <Route path="/costs" component={CostsRoute} />
      <Route path="/payments" component={PaymentsRoute} />
      <Route path="/client-alerts" component={ClientAlertsRoute} />
      <Route path="/client-analytics" component={ClientAnalyticsRoute} />
      <Route path="/client-analytics-overview" component={ClientAnalyticsOverviewRoute} />
      <Route path="/red-team" component={RedTeamRoute} />
      <Route path="/red-team-v2" component={RedTeamV2Route} />
      <Route path="/transaction-review" component={TransactionReviewRoute} />
      <Route path="/reconciliation" component={ReconciliationRoute} />
      <Route path="/clients" component={ClientsRoute} />
      <Route path="/ai-writing" component={AIWritingRoute} />
      <Route path="/ai-research" component={AIResearchRoute} />
      <Route path="/portfolio-report" component={PortfolioReportRoute} />
      <Route path="/pricing-calculator" component={PricingCalculatorRoute} />
      <Route path="/depreciation-worksheet" component={DepreciationWorksheetRoute} />
      <Route path="/team-notes" component={TeamNotesRoute} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/" component={HomeRedirect} />
      <Route component={NotFound} />
    </Switch>
  );
}

const FAVICON_SETTINGS_QUERY = { keys: ["site_favicon_url"] };

function applyFavicon(url: string) {
  if (!url || typeof document === "undefined") return;
  document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]').forEach((link) => {
    link.href = url;
  });
}

function FaviconLoader({ enabled }: { enabled: boolean }) {
  const { data } = trpc.appSettings.getMany.useQuery(FAVICON_SETTINGS_QUERY, {
    staleTime: 60_000,
    enabled,
  });
  useEffect(() => {
    if (data?.site_favicon_url) applyFavicon(data.site_favicon_url);
  }, [data?.site_favicon_url]);
  useEffect(() => {
    const onFaviconChange = (event: Event) => applyFavicon((event as CustomEvent<string>).detail);
    window.addEventListener("apx:favicon-change", onFaviconChange);
    return () => window.removeEventListener("apx:favicon-change", onFaviconChange);
  }, []);
  return null;
}

function AppInner() {
  const { user } = useAuth();
  useAutoReload();
  return <><FaviconLoader enabled={Boolean(user)} /><Router /></>;
}

// Stable reference — defined at module scope so the object identity never
// changes between renders. Inline object literals in JSX props create a new
// reference every render, which causes sonner's useSyncExternalStore to call
// forceStoreRerender on every render → React Error #185 infinite loop.
const TOAST_OPTIONS = {
  classNames: {
    info: "!border-[oklch(0.72_0.14_75)] !bg-[oklch(0.98_0.02_75)] !text-[oklch(0.18_0.04_240)] shadow-md",
    actionButton: "!bg-[oklch(0.72_0.14_75)] !text-[oklch(0.08_0.01_240)] font-semibold hover:!bg-[oklch(0.65_0.14_75)]",
    cancelButton: "!bg-transparent !text-[oklch(0.45_0.02_240)] hover:!text-[oklch(0.18_0.04_240)]",
  },
} as const;

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <PreviewProvider>
            <TooltipProvider>
              <Toaster
                position="top-right"
                richColors
                toastOptions={TOAST_OPTIONS}
              />
              <AppInner />
            </TooltipProvider>
          </PreviewProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
